import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/UI/Button';
import { ActionMenuItem } from '../../components/UI/ActionMenu';
import { ResponsiveEntityList } from '../../components/UI/ResponsiveEntityList';
import { Modal } from '../../components/UI/Modal';
import { contactService } from '../../services/contactService';
import { Contact } from '../../types/contact';
import { useToast } from '../../components/UI/ToastProvider';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

export const ContactsListPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phoneNumber: '', identifier: '' });
  const { addToast } = useToast();

  const fetchContacts = async () => {
    setLoading(true);
    try
    {
      const data = await contactService.getAll();
      setContacts(data);
    }
    catch
    {
      addToast('Erro ao carregar contatos', 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchContacts();
  }, []);

  const filtered = contacts.filter(c => {
    const term = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(term)
      || (c.email?.toLowerCase().includes(term) ?? false)
      || (c.phoneNumber?.includes(term) ?? false)
    );
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim())
    {
      addToast('Nome é obrigatório', 'error');
      return;
    }

    setModalLoading(true);
    try
    {
      await contactService.create({
        name: form.name.trim(),
        email: form.email || undefined,
        phoneNumber: form.phoneNumber || undefined,
        identifier: form.identifier || undefined,
      });
      addToast('Contato criado com sucesso', 'success');
      setIsModalOpen(false);
      setForm({ name: '', email: '', phoneNumber: '', identifier: '' });
      await fetchContacts();
    }
    catch
    {
      addToast('Erro ao criar contato', 'error');
    }
    finally
    {
      setModalLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete)
    {
      return;
    }

    try
    {
      await contactService.delete(contactToDelete.id);
      addToast('Contato excluído', 'success');
      setIsDeleteModalOpen(false);
      setContactToDelete(null);
      await fetchContacts();
    }
    catch (error)
    {
      if (axios.isAxiosError(error) && error.response?.status === 409)
      {
        const detail = (error.response.data as { detail?: string } | undefined)?.detail;
        addToast(
          detail || 'Não é possível excluir o contato: existem conversas vinculadas.',
          'error'
        );
        return;
      }

      addToast('Erro ao excluir contato', 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contatos</h1>
          <p className="text-gray-600 text-sm mt-1">Pessoas que interagem via canais de atendimento</p>
        </div>
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>Novo contato</Button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <input
          type="text"
          placeholder="Buscar contatos..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Nenhum contato encontrado.
        </div>
      ) : (
        <ResponsiveEntityList
          items={filtered}
          getKey={(contact) => contact.id}
          mobileTitle={(contact) => contact.name}
          mobileTo={(contact) => helpdeskRoutes.contact(contact.id)}
          mobileFields={(contact) => [
            { label: 'E-mail', value: contact.email || '—' },
            { label: 'Telefone', value: contact.phoneNumber || '—' },
            {
              label: 'Atividade',
              value: contact.lastActivityAt ? new Date(contact.lastActivityAt).toLocaleString('pt-BR') : '—',
            },
          ]}
          columns={[
            {
              header: 'Nome',
              cellClassName: 'px-6 py-4 text-sm font-medium text-gray-900',
              cell: (contact) => contact.name,
            },
            { header: 'E-mail', cell: (contact) => contact.email || '—' },
            { header: 'Telefone', cell: (contact) => contact.phoneNumber || '—' },
            {
              header: 'Última atividade',
              cell: (contact) =>
                contact.lastActivityAt ? new Date(contact.lastActivityAt).toLocaleString('pt-BR') : '—',
            },
          ]}
          actions={(contact): ActionMenuItem[] => [
            {
              key: 'view',
              label: 'Ver detalhes',
              to: helpdeskRoutes.contact(contact.id),
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ),
            },
            {
              key: 'delete',
              label: 'Excluir',
              variant: 'danger',
              onClick: () => {
                setContactToDelete(contact);
                setIsDeleteModalOpen(true);
              },
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              ),
            },
          ]}
        />
      )}

      <Modal isOpen={isModalOpen} title="Novo contato" onClose={() => setIsModalOpen(false)}>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <input type="text" value={form.phoneNumber} onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identificador</label>
            <input type="text" value={form.identifier} onChange={e => setForm(f => ({ ...f, identifier: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={modalLoading}>{modalLoading ? 'Salvando...' : 'Criar'}</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} title="Excluir contato" onClose={() => setIsDeleteModalOpen(false)}>
        <p className="text-gray-600 mb-4">Deseja excluir o contato <strong>{contactToDelete?.name}</strong>?</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Excluir</Button>
        </div>
      </Modal>
    </div>
  );
};
